import {
  MAX_PRESENTATION_FRAMES,
  PRESENTATION_FRAME_HEIGHT,
  PRESENTATION_FRAME_WIDTH,
  isPresentationFirstSlide,
} from "./presentation.js";

import type { SerializedExcalidrawElement } from "./domain.js";

/**
 * The one rule for what a student may change, shared verbatim by the server
 * (which enforces it) and the student's editor (which applies it before
 * sending, so a refused edit snaps back without a round trip). Teachers are
 * never restricted and never evaluate it.
 *
 * - Excalidraw's own index bookkeeping (z-order re-numbering) is not an edit.
 * - Frames are teacher-managed; a student may only add to their comments or
 *   toggle the seal (§5.3: the owner on a personal frame, anyone on a group
 *   one — the same ownership check the position rule below already makes).
 * - Teacher material (worksheet-tagged: everything a teacher creates),
 *   whatever the teacher deleted, and sealed frames are closed to students.
 * - In focus mode (집중모드) on an individual canvas (any frame has an
 *   owner), a student changes only their own elements, and every result
 *   must keep its center inside their own frame (spec 2026-09-28, decision
 *   b). Ownership is judged by position, not `frameId`: the core rewrites
 *   `frameId` from wherever the pointer ends a drag. A stray of theirs that
 *   already sits outside may still be deleted, edited in place or brought
 *   back. Focus mode is the default; help mode (도움모드) is on only while
 *   the teacher has flagged the frames with `helpMode`.
 * - Otherwise — a group canvas, or help mode — anything else goes.
 * - A presentation canvas (발표 캔버스, `presentation: true`) has one student
 *   and no seals, so its student lays out their own slides: frames may be
 *   added (slide size, up to `MAX_PRESENTATION_FRAMES`), moved and deleted,
 *   never resized or rotated, and the first slide is never deleted;
 *   everything else goes, the teacher's edits included.
 */

type SceneElement = SerializedExcalidrawElement;

type Box = { minX: number; minY: number; maxX: number; maxY: number };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const canvaschoolData = (element: SceneElement) => {
  const customData = element.customData;
  return isRecord(customData) && isRecord(customData.canvaschool)
    ? customData.canvaschool
    : null;
};

export const isFrameElement = (element: SceneElement): boolean =>
  element.type === "frame" || element.type === "magicframe";

/** The student number a personal frame belongs to; null on a group frame. */
export const frameOwner = (frame: SceneElement): number | null => {
  const owner = canvaschoolData(frame)?.owner;
  return typeof owner === "number" && Number.isInteger(owner) && owner > 0
    ? owner
    : null;
};

/** The frame carries a seal (마감) mark. */
export const isSealed = (frame: SceneElement): boolean => {
  const sealed = canvaschoolData(frame)?.sealed;
  return isRecord(sealed) && typeof sealed.at === "string";
};

/** The teacher switched this individual canvas to help mode (도움모드). */
export const isHelpMode = (frame: SceneElement): boolean =>
  canvaschoolData(frame)?.helpMode === true;

/** The teacher deleted it; the teacher's editor marks every deletion. */
export const isDeletedByTeacher = (element: SceneElement): boolean =>
  element.isDeleted && canvaschoolData(element)?.deletedByTeacher === true;

/** Teacher material: the teacher's editor tags everything it creates. */
export const isWorksheetElement = (element: SceneElement): boolean =>
  canvaschoolData(element)?.worksheet === true;

const frameIdOf = (element: SceneElement): string | null =>
  typeof element.frameId === "string" ? element.frameId : null;

const finite = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

/** Axis-aligned bounds, rotation included; points decide for lines and
 * freedraw, whose origin need not be their corner. */
const boundsOf = (element: SceneElement): Box => {
  const x = finite(element.x);
  const y = finite(element.y);
  let minX = x;
  let minY = y;
  let maxX = x + finite(element.width);
  let maxY = y + finite(element.height);
  if (Array.isArray(element.points) && element.points.length > 0) {
    minX = minY = Infinity;
    maxX = maxY = -Infinity;
    for (const point of element.points) {
      const px = x + finite(Array.isArray(point) ? point[0] : 0);
      const py = y + finite(Array.isArray(point) ? point[1] : 0);
      minX = Math.min(minX, px);
      minY = Math.min(minY, py);
      maxX = Math.max(maxX, px);
      maxY = Math.max(maxY, py);
    }
  }
  if (minX > maxX) {
    [minX, maxX] = [maxX, minX];
  }
  if (minY > maxY) {
    [minY, maxY] = [maxY, minY];
  }
  const angle = finite(element.angle);
  if (angle === 0) {
    return { minX, minY, maxX, maxY };
  }
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const corners = [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ].map(([px, py]) => [
    cx + (px - cx) * cos - (py - cy) * sin,
    cy + (px - cx) * sin + (py - cy) * cos,
  ]);
  return {
    minX: Math.min(...corners.map(([px]) => px)),
    minY: Math.min(...corners.map(([, py]) => py)),
    maxX: Math.max(...corners.map(([px]) => px)),
    maxY: Math.max(...corners.map(([, py]) => py)),
  };
};

/** Excalidraw keeps an element in a frame while the two overlap at all. */
const overlaps = (left: Box, right: Box): boolean =>
  left.minX <= right.maxX &&
  left.maxX >= right.minX &&
  left.minY <= right.maxY &&
  left.maxY >= right.minY;

/** Absent and empty mean the same: the core fills `boundElements: null` in
 * as `[]` when it loads a scene, and that is not an edit. */
const isEmpty = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (Array.isArray(value) && value.length === 0);

/** Whether Excalidraw would still count `element` as inside `frame`. */
export const overlapsFrame = (
  element: SceneElement,
  frame: SceneElement,
): boolean => overlaps(boundsOf(element), boundsOf(frame));

const equal = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right) || (isEmpty(left) && isEmpty(right))) {
    return true;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((item, index) => equal(item, right[index]))
    );
  }
  if (!isRecord(left) || !isRecord(right)) {
    return false;
  }
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every((key) => equal(left[key], right[key]));
};

const without = (
  element: SceneElement,
  keys: readonly string[],
): Record<string, unknown> => {
  const rest: Record<string, unknown> = { ...element };
  for (const key of keys) {
    delete rest[key];
  }
  return rest;
};

const BOOKKEEPING = ["index", "version", "versionNonce", "updated"] as const;

/** Whether `after` differs from `before` only in the core's z-order and
 * revision bookkeeping — not an edit anyone made. */
export const isBookkeepingOnly = (
  before: SceneElement,
  after: SceneElement,
): boolean => equal(without(before, BOOKKEEPING), without(after, BOOKKEEPING));

/** Strips one frame-self-info field (spec §5.2: "마감 여부·댓글은 [되돌리는
 * 규칙의] 대상이 아니다") so a change touching only that field doesn't read
 * as an edit to the frame itself. */
const withoutField = (
  element: SceneElement,
  field: "comments" | "sealed" | "background",
): Record<string, unknown> => {
  const rest = without(element, BOOKKEEPING);
  const data = canvaschoolData(element);
  if (data) {
    const { [field]: _dropped, ...canvaschool } = data;
    rest.customData = {
      ...(element.customData as Record<string, unknown>),
      canvaschool,
    };
  }
  return rest;
};

/**
 * True when the only thing separating `before` and `after` is a comment or
 * a seal toggle — never both at once. Checking each field in isolation (not
 * dropping both together) keeps a mutation that smuggles a seal or owner
 * change in alongside an allowed comment from reading as exempt.
 */
const isFrameSelfInfoOnlyChange = (before: SceneElement, after: SceneElement) =>
  equal(withoutField(before, "comments"), withoutField(after, "comments")) ||
  equal(withoutField(before, "sealed"), withoutField(after, "sealed"));

/** A student recolouring a frame's background and nothing else. */
const isBackgroundOnlyChange = (before: SceneElement, after: SceneElement) =>
  equal(withoutField(before, "background"), withoutField(after, "background"));

const commentsOf = (frame: SceneElement): Record<string, unknown>[] => {
  const comments = canvaschoolData(frame)?.comments;
  return Array.isArray(comments)
    ? (comments.filter(isRecord) as Record<string, unknown>[])
    : [];
};

/**
 * A comment-only change on a frame that was already sealed, in which every
 * added, edited or removed comment is this student's own (spec 2026-09-16
 * §6.1: on an individual canvas anyone may comment on a friend's sealed
 * frame, but only ever as themselves).
 */
const isOwnCommentOnSealedFrame = (
  before: SceneElement,
  after: SceneElement,
  studentNumber: number | null,
): boolean => {
  if (
    studentNumber === null ||
    !isSealed(before) ||
    !equal(withoutField(before, "comments"), withoutField(after, "comments"))
  ) {
    return false;
  }
  const old = commentsOf(before);
  const next = commentsOf(after);
  const mine = (comment: Record<string, unknown>) =>
    comment.authorRole === "STUDENT" && comment.authorNumber === studentNumber;
  const touched = [
    ...next.filter((c) => !old.some((o) => equal(o, c))),
    ...old.filter((o) => !next.some((c) => equal(o, c))),
  ];
  return touched.every(mine);
};

/** Why the student rule refused a change (spec 2026-09-28 §7). */
export type StudentRefusal =
  /** A frame's structure: only the teacher lays out frames. */
  | "frame"
  /** Teacher material, or something the teacher deleted. */
  | "teacher"
  /** A sealed (마감) frame. */
  | "sealed"
  /** Someone else's element (in focus mode). */
  | "notMine"
  /** Would leave, or be made outside, the student's own frame. */
  | "outside";

export type StudentChangeVerdict = (
  before: SceneElement | undefined,
  after: SceneElement,
) => StudentRefusal | null;

export type StudentChangeCheck = (
  before: SceneElement | undefined,
  after: SceneElement,
) => boolean;

/** The owner tag a student's editor (and the server, as a backstop) stamps
 * on every element that student creates. */
export const ownerTagOf = (element: SceneElement): number | null => {
  const owner = canvaschoolData(element)?.owner;
  return typeof owner === "number" && Number.isInteger(owner) && owner > 0
    ? owner
    : null;
};

/** `element` with its owner tag set to `owner`. */
export const withOwnerTag = <T extends SceneElement>(
  element: T,
  owner: number,
): T => {
  const customData = isRecord(element.customData) ? element.customData : {};
  const canvaschool = isRecord(customData.canvaschool)
    ? customData.canvaschool
    : {};
  return {
    ...element,
    customData: { ...customData, canvaschool: { ...canvaschool, owner } },
  };
};

const centerOf = (element: SceneElement) => {
  const box = boundsOf(element);
  return { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 };
};

const contains = (box: Box, point: { x: number; y: number }) =>
  point.x >= box.minX &&
  point.x <= box.maxX &&
  point.y >= box.minY &&
  point.y <= box.maxY;

/** Distance from a point to a box, 0 inside it. */
const distanceTo = (box: Box, point: { x: number; y: number }) =>
  Math.hypot(
    Math.max(box.minX - point.x, 0, point.x - box.maxX),
    Math.max(box.minY - point.y, 0, point.y - box.maxY),
  );

/** Whether `element`'s center lies inside `frame` — the one membership test
 * the student rule uses (decision b). */
export const centerInFrame = (
  element: SceneElement,
  frame: SceneElement,
): boolean => contains(boundsOf(frame), centerOf(element));

/**
 * The frame an element belongs to by position: the one its center lies in
 * (its own `frameId` frame first, should frames ever overlap), or null. This
 * is what an editor writes into `frameId` once a local edit settles — the
 * core's own `frameId` follows the *pointer* at the end of a drag, not the
 * element.
 */
export const frameByPosition = (
  element: SceneElement,
  frames: Iterable<SceneElement>,
): SceneElement | null => {
  const center = centerOf(element);
  let found: SceneElement | null = null;
  for (const frame of frames) {
    if (!isFrameElement(frame) || frame.isDeleted) {
      continue;
    }
    if (contains(boundsOf(frame), center)) {
      if (frame.id === element.frameId) {
        return frame;
      }
      found ??= frame;
    }
  }
  return found;
};

/**
 * Builds the rule for one student against a scene's current frames, naming
 * why a change is refused. `before` is the element's current revision
 * (undefined when new), `after` the one the student wants in its place.
 *
 * In focus mode an element belongs to the owner of the student frame its
 * center lies in; one that sits in no student frame keeps its owner tag (or,
 * for data from before the tag, its `frameId` frame's owner). `frameId` never
 * decides on its own: the core rewrites it from the pointer position.
 */
export type StudentChangeOptions = {
  /** The canvas is a presentation lesson student's own (발표 캔버스). */
  presentation?: boolean;
};

const numberOr = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

/** The presentation-canvas rule (see the header): only frame shape and the
 * slide limit are closed. */
const presentationChangeVerdict = (
  current: Iterable<SceneElement>,
): StudentChangeVerdict => {
  let liveFrames = 0;
  for (const element of current) {
    if (isFrameElement(element) && !element.isDeleted) {
      liveFrames += 1;
    }
  }
  return (before, after) => {
    if (before && isBookkeepingOnly(before, after)) {
      return null;
    }
    if (!isFrameElement(after) && !(before && isFrameElement(before))) {
      return null;
    }
    if (after.isDeleted) {
      return before && !before.isDeleted && isPresentationFirstSlide(before)
        ? "frame"
        : null;
    }
    const revived = !before || before.isDeleted;
    if (revived && liveFrames >= MAX_PRESENTATION_FRAMES) {
      return "frame";
    }
    if (
      numberOr(after.width, NaN) === PRESENTATION_FRAME_WIDTH &&
      numberOr(after.height, NaN) === PRESENTATION_FRAME_HEIGHT &&
      numberOr(after.angle, 0) === 0
    ) {
      return null;
    }
    // A live frame keeps whatever shape it already has; it may still move.
    return before &&
      !revived &&
      numberOr(before.width, NaN) === numberOr(after.width, NaN) &&
      numberOr(before.height, NaN) === numberOr(after.height, NaN) &&
      numberOr(before.angle, 0) === numberOr(after.angle, 0)
      ? null
      : "frame";
  };
};

export const studentChangeVerdict = (
  current: Iterable<SceneElement>,
  studentNumber: number | null,
  options: StudentChangeOptions = {},
): StudentChangeVerdict => {
  if (options.presentation) {
    return presentationChangeVerdict(current);
  }
  const frames = new Map<string, SceneElement>();
  for (const element of current) {
    if (isFrameElement(element) && !element.isDeleted) {
      frames.set(element.id, element);
    }
  }
  const ownedFrames = [...frames.values()].filter(
    (frame) => frameOwner(frame) !== null,
  );
  const individual = ownedFrames.length > 0;
  const focused =
    individual && ![...frames.values()].some((frame) => isHelpMode(frame));
  const owns = (frame: SceneElement) =>
    studentNumber !== null && frameOwner(frame) === studentNumber;
  const myFrame =
    studentNumber === null
      ? undefined
      : ownedFrames.find((frame) => frameOwner(frame) === studentNumber);
  const frameOf = (element: SceneElement) => {
    const frameId = frameIdOf(element);
    return frameId === null ? undefined : frames.get(frameId);
  };
  const inSealedFrame = (element: SceneElement) => {
    const byId = frameOf(element);
    const byPosition = frameByPosition(element, frames.values());
    return (
      (byId !== undefined && isSealed(byId)) ||
      (byPosition !== null && isSealed(byPosition))
    );
  };
  const ownerOf = (element: SceneElement): number | null => {
    const home = frameByPosition(element, ownedFrames);
    if (home) {
      return frameOwner(home);
    }
    const tag = ownerTagOf(element);
    if (tag !== null) {
      return tag;
    }
    const byId = frameOf(element);
    return byId ? frameOwner(byId) : null;
  };

  return (before, after) => {
    if (before && isBookkeepingOnly(before, after)) {
      return null;
    }
    if (isFrameElement(after) || (before && isFrameElement(before))) {
      return before !== undefined &&
        isFrameElement(before) &&
        ((isFrameSelfInfoOnlyChange(before, after) &&
          (!focused || owns(before))) ||
          isOwnCommentOnSealedFrame(before, after, studentNumber) ||
          // the background is the owner's alone, in help mode too
          (isBackgroundOnlyChange(before, after) &&
            owns(before) &&
            !isSealed(before)))
        ? null
        : "frame";
    }
    if (
      (before && (isWorksheetElement(before) || isDeletedByTeacher(before))) ||
      isWorksheetElement(after)
    ) {
      return "teacher";
    }
    if (!focused) {
      return (before && inSealedFrame(before)) || inSealedFrame(after)
        ? "sealed"
        : null;
    }
    if (before && ownerOf(before) !== studentNumber) {
      return "notMine";
    }
    if (!myFrame) {
      return "outside";
    }
    if (isSealed(myFrame)) {
      return "sealed";
    }
    if (after.isDeleted || centerInFrame(after, myFrame)) {
      return null;
    }
    // A stray of mine already outside my frame: it may be edited in place
    // or brought back, never carried further away.
    if (before && !before.isDeleted && !centerInFrame(before, myFrame)) {
      const box = boundsOf(myFrame);
      return distanceTo(box, centerOf(after)) <=
        distanceTo(box, centerOf(before)) + 0.5
        ? null
        : "outside";
    }
    return "outside";
  };
};

/** `studentChangeVerdict` as a yes/no check. */
export const studentChangePolicy = (
  current: Iterable<SceneElement>,
  studentNumber: number | null,
  options: StudentChangeOptions = {},
): StudentChangeCheck => {
  const verdict = studentChangeVerdict(current, studentNumber, options);
  return (before, after) => verdict(before, after) === null;
};
